import React, { FC, memo, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import Autocomplete from "Components/Autocomplete";
import { areEqualShallow } from "Utils/areEqual";

interface Props {
  isStartingPoint: boolean;
  suggestions: any[];
  placeholder: string;
  selectedInputValue: string;
  setShowDirections: (setShowDirections: boolean) => void;
  setUserSelection: (val: any) => void;
  directionsRef: any;
}
const AutoCompleteContainer: FC<Props> = memo(
  ({
    isStartingPoint,
    suggestions,
    setShowDirections,
    placeholder = "Choose starting point",
    selectedInputValue = "",
    setUserSelection, // Pass the use selection up to the parent
    directionsRef,
  }: Props) => {
    const view = useSelector((store: any) => store.searchOptionsView.view);
    const [inputValue, setValue] = useState(selectedInputValue);
    const [suggestionMenu, showSuggestionMenu] = useState(false);
    const [activeSuggestion, setActiveSuggestion] = useState(-1);
    const [filteredSuggestion, setFilteredSuggestion] = useState([]);
    const [dropDown, setDropDown] = useState(false);
    const inputRef = useRef(null);
    // This is used to allow the user to navigate through the suggestions list, highlighting
    // Each entry as they use the up/down arrow keys.
    const [hoverFocusedSuggestion, setHoverFocusedSuggestion] = useState(
      selectedInputValue
    );
    const setInputValue = (val: string) => setValue(val);

    // Let's check and see if the selectedInputValue has changed and reset the useSate
    // TODO:  Remove this step and just rely on the selected suggestion
    useEffect(() => {
      setValue(selectedInputValue);
    }, [selectedInputValue]);

    const onChange = (e: any) => {
      const userInput = e.currentTarget.value;
      const userInputLowerCase = userInput.toLocaleLowerCase();
      const matchingSuggestions = suggestions.filter(
        (suggestion: any) =>
          suggestion.title.toLocaleLowerCase().indexOf(userInputLowerCase) > -1
      );

      setFilteredSuggestion(matchingSuggestions);
      showSuggestionMenu(true);
      setInputValue(userInput);
    };

    // Event fired when the user clicks on a suggestion
    const onClick = (e: any) => {
      const text = e.currentTarget.innerText;
      setActiveSuggestion(-1);
      setFilteredSuggestion([]);
      showSuggestionMenu(false);
      setDropDown(false);
      setInputValue(text);
      const selected = suggestions.find(
        (suggestion: any) => suggestion.title === text
      );
      if (selected) {
        setUserSelection({
          id: selected.id,
          title: selected.title,
          realLat: selected.realLat,
          realLng: selected.realLng,
          customLat: selected.customLat,
          customLng: selected.customLng,
        });
      }
    };

    const clickOutside = () => {
      setActiveSuggestion(-1);
      setFilteredSuggestion([]);
      showSuggestionMenu(false);
      setDropDown(false);
    };
    const onKeyUp = (e: any) => {
      let activeIndex = 0; // Defaults to the first element in the filteredSuggestion array;
      // Use e.code, not e.keyCode. It's deprecated
      const suggestionList =
        filteredSuggestion.length > 0 ? filteredSuggestion : suggestions;

      switch (e.code) {
        case "NumpadEnter":
        case "Enter": {
          if (activeSuggestion === -1 && view === "directions") {
            setDropDown(true);
            return;
          }
          // Todo:  Reset it to something other than 0.
          setActiveSuggestion(-1);
          showSuggestionMenu(false);
          const selectedSuggestion = suggestionList[activeSuggestion];
          setInputValue(selectedSuggestion.title);
          setUserSelection({
            id: selectedSuggestion.id,
            title: selectedSuggestion.title,
            realLat: selectedSuggestion.realLat,
            realLng: selectedSuggestion.realLng,
            customLat: selectedSuggestion.customLat,
            customLng: selectedSuggestion.customLng,
          });
          setHoverFocusedSuggestion("");
          setDropDown(false);
          break;
        }
        case "ArrowUp":
          if (activeSuggestion <= 0) {
            // We are gone past the start and will now go to the end of the list
            activeIndex = suggestionList.length - 1;
          } else {
            // Move up the list towards the beginning
            activeIndex = activeSuggestion - 1;
          }

          setActiveSuggestion(activeIndex);
          setHoverFocusedSuggestion(suggestionList[activeIndex].title);
          break;
        case "ArrowDown":
          if (!dropDown) setDropDown(true);
          if (activeSuggestion < suggestionList.length - 1) {
            // We are gone past the start and will now go to the end of the list
            activeIndex = activeSuggestion + 1;
          } else if (suggestionList.length - 1) {
            // We've come to the end.  Go to the beginning
            activeIndex = 0;
          }
          setActiveSuggestion(activeIndex);
          setHoverFocusedSuggestion(suggestionList[activeIndex].title);
          break;
        case "Escape":
          setActiveSuggestion(-1);
          setFilteredSuggestion([]);
          showSuggestionMenu(false);
          setHoverFocusedSuggestion("");
          setDropDown(false);
          break;
        default:
          break;
      }
    };

    const onDropdownClick = () => {
      setDropDown((val) => !val);
    };

    return (
      <Autocomplete
        isStartingPoint={isStartingPoint}
        placeholder={placeholder}
        inputValue={inputValue}
        hoverFocusedSuggestion={hoverFocusedSuggestion}
        suggestionMenu={suggestionMenu}
        filteredSuggestion={filteredSuggestion}
        onChange={onChange}
        onClick={onClick}
        clickOutside={clickOutside}
        onKeyUp={onKeyUp}
        inputRef={inputRef}
        onFocus={() => setShowDirections(true)}
        dropDown={dropDown}
        onDropdownClick={onDropdownClick}
        suggestions={suggestions}
        directionsRef={directionsRef}
      />
    );
  },
  areEqualShallow
);

export default AutoCompleteContainer;
